import type { TFunction } from 'i18next'

import type { PaymentStatus } from 'shared/api/kyrgyzstan/orders'
import { RealtySource } from 'shared/api/kyrgyzstan/search/model'
import { CurrencySom } from 'shared/constants'
import { priceShow } from 'shared/lib/priceShow'

export interface BookingPaymentOption {
  id: PaymentStatus
  title: string
  amount: number
  description?: string
  bonusText?: string
}

interface GetBookingPaymentOptionsParams {
  totalPrice: number
  prepaymentPrice: number
  source?: RealtySource
  mbonusAvailable: boolean
  mbonusAmount: number
  t: TFunction<'translation', undefined>
}

interface CreateFullPaymentOptionParams {
  totalPrice: number
  mbonusAvailable: boolean
  mbonusAmount: number
  t: TFunction<'translation', undefined>
}

interface CreatePrepaymentOptionParams {
  totalPrice: number
  prepaymentPrice: number
  t: TFunction<'translation', undefined>
}

const defaultAvailablePaymentStatuses = [
  'full',
  'prepayment',
] as const satisfies readonly PaymentStatus[]

const availablePaymentStatusesBySource = {
  [RealtySource.INTERNAL]: ['full', 'prepayment'],
  [RealtySource.EXELY]: ['full'],
  [RealtySource.STAYBOOK]: ['full'],
  [RealtySource.WEBVIEW]: ['prepayment'],
  [RealtySource.RATEHAWK]: ['full'],
} satisfies Record<RealtySource, readonly PaymentStatus[]>

const getAvailablePaymentStatuses = (source?: RealtySource) =>
  source
    ? (availablePaymentStatusesBySource[source] ??
      defaultAvailablePaymentStatuses)
    : defaultAvailablePaymentStatuses

const isValidPrepayment = (totalPrice: number, prepaymentPrice: number) =>
  prepaymentPrice > 0 && prepaymentPrice < totalPrice

const createFullPaymentOption = ({
  totalPrice,
  mbonusAvailable,
  mbonusAmount,
  t,
}: CreateFullPaymentOptionParams): BookingPaymentOption => {
  const option: BookingPaymentOption = {
    id: 'full',
    amount: totalPrice,
    title: t('kyrgyzstan_block.booking.payment.full.title'),
  }

  if (mbonusAvailable && mbonusAmount > 0) {
    option.bonusText = t('kyrgyzstan_block.booking.payment.full.mbonus', {
      percent: '10',
      total: priceShow(String(mbonusAmount)),
    })
  }

  return option
}

const createPrepaymentOption = ({
  totalPrice,
  prepaymentPrice,
  t,
}: CreatePrepaymentOptionParams): BookingPaymentOption => {
  const additionalAmount = totalPrice - prepaymentPrice

  return {
    id: 'prepayment',
    amount: prepaymentPrice,
    title: t('kyrgyzstan_block.booking.payment.prepayment.title'),
    description: t('kyrgyzstan_block.booking.payment.prepayment.description', {
      total: priceShow(String(additionalAmount)),
      currency: CurrencySom,
    }),
  }
}

export const getBookingPaymentOptions = ({
  totalPrice,
  prepaymentPrice,
  source,
  mbonusAvailable,
  mbonusAmount,
  t,
}: GetBookingPaymentOptionsParams): BookingPaymentOption[] => {
  const paymentOptions: BookingPaymentOption[] = []

  for (const paymentStatus of getAvailablePaymentStatuses(source)) {
    if (paymentStatus === 'full' && totalPrice > 0) {
      paymentOptions.push(
        createFullPaymentOption({
          totalPrice,
          mbonusAvailable,
          mbonusAmount,
          t,
        }),
      )
    }

    if (
      paymentStatus === 'prepayment' &&
      isValidPrepayment(totalPrice, prepaymentPrice)
    ) {
      paymentOptions.push(
        createPrepaymentOption({ totalPrice, prepaymentPrice, t }),
      )
    }
  }

  return paymentOptions
}
